// Stories 2.8, 4.1, 4.3, 4.5, 5.8 — Claim Detail Page
import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  Car,
  Receipt as ReceiptIcon,
  Trash2,
  Send,
  RotateCw,
  AlertTriangle,
  Pencil,
  X,
  Check,
  Flag,
  FileCheck,
  Info,
} from 'lucide-react';
import {
  useClaim,
  useUpdateClaim,
  useExpenses,
  useDeleteMileage,
  useDeletePettyCash,
  useDeleteClaim,
  useSubmitClaim,
  useSystemConfig,
  describeFlagger,
  type ExpenseItem,
} from '../hooks/useClaims';
import { useResubmitClaim } from '../hooks/useApproval';
import { MileageFormModal } from '../components/claims/MileageFormModal';
import { PettyCashFormModal } from '../components/claims/PettyCashFormModal';
import { ConfirmDialog } from '../components/claims/ConfirmDialog';
import { ValidationErrors, type ValidationError } from '../components/claims/ValidationErrors';
import { ClaimStatusProgress } from '../components/features/claims/ClaimStatusProgress';
import { Card, Button, ClaimStatusBadge } from '@rhpc/ui';
import { PageShell } from '@/components/PageShell';
import { ApiError } from '../lib/api';
import { AnimatePresence } from 'motion/react';
import { ClaimTypeBadge } from './ClaimsPage';
import { plural } from '@/lib/plural';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

function formatDate(isoDate: string) {
  return new Date(isoDate).toLocaleDateString('en-MY', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatMonth(isoDate: string) {
  return new Date(isoDate).toLocaleDateString('en-MY', { year: 'numeric', month: 'long' });
}

// ---------------------------------------------------------------------------
// Modal state type
// ---------------------------------------------------------------------------

type ModalState =
  | { type: 'none' }
  | { type: 'mileage-create' }
  | { type: 'mileage-edit'; entryId: string }
  | { type: 'petty-cash-create' }
  | { type: 'petty-cash-edit'; entryId: string }
  | { type: 'delete-confirm'; expenseType: 'mileage' | 'petty-cash'; entryId: string; label: string }
  | { type: 'delete-claim-confirm' }
  | { type: 'submit-confirm' }
  | { type: 'resubmit-confirm' }
  | { type: 'duplicate-claim'; existingClaimId: string; referenceNumber: string | null; monthLabel: string };

function EmptyClaimHint() {
  return (
    <p id="empty-claim-hint" className="flex items-center gap-1.5 text-xs text-text-2">
      <Info className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden="true" />
      Add at least one expense before submitting.
    </p>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function ClaimDetailPage() {
  const { claimId } = useParams<{ claimId: string }>();
  const navigate = useNavigate();

  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [resubmitErrors, setResubmitErrors] = useState<ValidationError[]>([]);

  const { data: claimData, isLoading: claimLoading } = useClaim(claimId!);
  const { data: expensesData, isLoading: expensesLoading } = useExpenses(claimId!);
  const { data: systemConfigData } = useSystemConfig();
  const deleteMileage = useDeleteMileage(claimId!);
  const deletePettyCash = useDeletePettyCash(claimId!);
  const deleteClaim = useDeleteClaim();
  const submitClaim = useSubmitClaim(claimId!);
  const resubmitClaim = useResubmitClaim(claimId!);
  const updateClaim = useUpdateClaim(claimId!);

  const claim = claimData?.claim;
  const isDraft = claim?.status === 'DRAFT';
  const isRejected = claim?.status === 'REJECTED';
  const isEditable = isDraft || isRejected;
  const claimWithRejection = claim as (typeof claim & { rejectionReason?: string | null }) | undefined;
  const summary = expensesData?.summary;

  // Validation results go stale as soon as the expenses change (added/edited/deleted)
  // Keyed on content (not object identity) so a no-op refetch does not wipe a fresh submit result.
  const expensesSignature = JSON.stringify(expensesData?.expenses ?? null);
  useEffect(() => {
    setValidationErrors([]);
    setResubmitErrors([]);
  }, [expensesSignature]);

  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState('');
  const [isSavingDesc, setIsSavingDesc] = useState(false);

  const handleStartEditDesc = () => {
    setDescValue(claim?.description ?? '');
    setIsEditingDesc(true);
  };

  const handleSaveDesc = async () => {
    setIsSavingDesc(true);
    try {
      await updateClaim.mutateAsync({ description: descValue.trim() || null });
      setIsEditingDesc(false);
    } catch {
      // ignore
    } finally {
      setIsSavingDesc(false);
    }
  };

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleDelete = async (expenseType: 'mileage' | 'petty-cash', entryId: string) => {
    setDeleteError(null);
    try {
      if (expenseType === 'mileage') {
        await deleteMileage.mutateAsync(entryId);
      } else {
        await deletePettyCash.mutateAsync(entryId);
      }
      setModal({ type: 'none' });
    } catch (err) {
      if (err instanceof ApiError) {
        setDeleteError(err.message);
      }
    }
  };

  const handleDeleteClaim = async () => {
    if (!claimId) return;
    setDeleteError(null);
    try {
      await deleteClaim.mutateAsync(claimId);
      navigate('/claims');
    } catch (err) {
      if (err instanceof ApiError) {
        setDeleteError(err.message);
      } else {
        setDeleteError('Failed to delete claim. Please try again.');
      }
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!claimId) return;
    setIsSubmitting(true);
    setValidationErrors([]);

    if (!sessionStorage.getItem(`claim_start_${claimId}`)) {
      sessionStorage.setItem(`claim_start_${claimId}`, String(Date.now()));
    }

    try {
      await submitClaim.mutateAsync();
      navigate(`/claims/${claimId}/confirmation`);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 422 && err.code === 'VALIDATION_FAILED') {
          const errors = (err.payload?.errors ?? []) as ValidationError[];
          setValidationErrors(errors);
          setTimeout(() => {
            document.getElementById('validation-errors')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        } else if (err.status === 409 && err.code === 'DUPLICATE_SUBMISSION') {
          setModal({
            type: 'duplicate-claim',
            existingClaimId: (err.payload?.existingClaimId as string) ?? '',
            referenceNumber: (err.payload?.referenceNumber as string | null) ?? null,
            monthLabel: claim ? formatMonth(claim.claimMonth) : 'this period',
          });
        } else {
          setDeleteError(err.message);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [claimId, submitClaim, navigate, claim]);

  const handleResubmit = useCallback(async () => {
    if (!claimId) return;
    setIsResubmitting(true);
    setResubmitErrors([]);
    try {
      await resubmitClaim.mutateAsync();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 422 && err.code === 'VALIDATION_FAILED') {
          const errors = (err.payload?.errors ?? []) as ValidationError[];
          setResubmitErrors(errors);
        } else {
          setDeleteError(err.message);
        }
      }
    } finally {
      setIsResubmitting(false);
    }
  }, [claimId, resubmitClaim]);

  // -------------------------------------------------------------------------
  // Loading / not-found states
  // -------------------------------------------------------------------------

  if (claimLoading) {
    return (
      <PageShell>
        <Card className="mb-6 p-[24px_28px]">
          <div className="skeleton h-[11px] w-[110px] rounded" />
          <div className="skeleton mt-[10px] h-6 w-[180px] rounded-[5px]" />
          <div className="skeleton mt-5 h-[34px] w-40 rounded-md" />
          <div className="mt-5 grid grid-cols-2 gap-4">
            <div className="skeleton h-[88px] rounded-md" />
            <div className="skeleton h-[88px] rounded-md" />
          </div>
        </Card>
        <div className="skeleton mb-6 h-[76px] rounded-lg" />
        <div className="skeleton mb-4 h-4 w-[130px] rounded-[5px]" />
        <div className="skeleton h-[180px] rounded-lg" />
      </PageShell>
    );
  }

  if (!claim) {
    return (
      <PageShell>
        <div className="py-12 text-center">
          <p className="text-text-2">Claim not found</p>
          <button onClick={() => navigate('/claims')} className="mt-3 text-sm text-primary underline">
            ← Back to Claims
          </button>
        </div>
      </PageShell>
    );
  }

  const itemCount = summary ? summary.mileageCount + summary.pettyCashCount : 0;
  const hasNoItems = !expensesLoading && itemCount === 0;
  const claimType = claim?.claimType ?? 'GENERAL';
  const canAddMileage = claimType !== 'PETTY_CASH';
  const isMileageClaim = claimType === 'MILEAGE';

  return (
    <PageShell>
      {/* Breadcrumb */}
      <div className="mb-6 flex items-center gap-2 text-[13px]">
        <button onClick={() => navigate('/claims')} className="text-text-2 transition-colors hover:text-text">
          My Claims
        </button>
        <ChevronRight className="h-[13px] w-[13px] text-text-2" strokeWidth={2} />
        <span className="font-semibold">{formatMonth(claim.claimMonth)}</span>
      </div>

      {/* Status progress (non-DRAFT claims) */}
      {claim.status !== 'DRAFT' && (
        <div className="mb-6">
          <ClaimStatusProgress
            claimId={claim.id}
            status={claim.status}
            rejectionReason={claimWithRejection?.rejectionReason}
            hardcopyReceived={claim.hardcopyReceived}
          />
        </div>
      )}

      {/* Claim summary card */}
      <Card className="mb-6 p-4 sm:p-[24px_28px]">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-text-2">
              Claim period
            </div>
            <div className="flex flex-wrap items-center gap-2.5 mt-1">
              <h1 className="text-2xl font-bold leading-tight tracking-tight">{formatMonth(claim.claimMonth)}</h1>
              <ClaimTypeBadge type={claim.claimType} />
            </div>

            {/* Description display & edit */}
            {!isEditingDesc ? (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {claim.description ? (
                  <p className="text-sm text-text break-words">{claim.description}</p>
                ) : (
                  isEditable && (
                    <span className="text-xs italic text-text-2">No description provided</span>
                  )
                )}
                {isEditable && (
                  <button
                    type="button"
                    onClick={handleStartEditDesc}
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-text-2 hover:bg-surface-2 hover:text-text"
                  >
                    <Pencil className="h-3 w-3" />
                    {claim.description ? 'Edit' : '+ Add description'}
                  </button>
                )}
              </div>
            ) : (
              <div className="mt-3 flex flex-col gap-2 w-full">
                <input
                  type="text"
                  value={descValue}
                  onChange={(e) => setDescValue(e.target.value)}
                  placeholder="Describe the purpose of this claim..."
                  maxLength={500}
                  className="h-9 w-full rounded-md border border-input bg-surface px-3 text-sm text-text focus:outline-2 focus:outline-primary"
                />
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    className="h-7 px-3 text-xs"
                    onClick={handleSaveDesc}
                    disabled={isSavingDesc}
                  >
                    {isSavingDesc ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-3 text-xs"
                    onClick={() => setIsEditingDesc(false)}
                    disabled={isSavingDesc}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div id="claim-status-badge">
            <ClaimStatusBadge status={claim.status} />
          </div>
        </div>

        <div className="mt-[18px] flex items-baseline gap-2">
          <span className="font-mono text-[32px] font-semibold tracking-tight">
            RM {formatCurrency(Number(claim.totalAmount))}
          </span>
          <span className="text-[13px] text-text-2">total</span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:gap-4">
          <div className="rounded-md border border-border p-3 sm:p-[14px_16px]">
            <div className="flex items-center gap-2 text-[13px] font-medium text-text-2">
              <Car className="h-[15px] w-[15px]" strokeWidth={2} />
              Mileage
            </div>
            <div className="mt-2 font-mono text-lg font-semibold">
              RM {formatCurrency(summary?.mileageTotal ?? 0)}
            </div>
            <div className="mt-0.5 text-xs text-text-2">
              {plural(summary?.mileageCount ?? 0, 'trip')}
            </div>
          </div>
          <div className="rounded-md border border-border p-3 sm:p-[14px_16px]">
            <div className="flex items-center gap-2 text-[13px] font-medium text-text-2">
              <ReceiptIcon className="h-[15px] w-[15px]" strokeWidth={2} />
              Petty cash
            </div>
            <div className="mt-2 font-mono text-lg font-semibold">
              RM {formatCurrency(summary?.pettyCashTotal ?? 0)}
            </div>
            <div className="mt-0.5 text-xs text-text-2">
              {plural(summary?.pettyCashCount ?? 0, 'item')}
            </div>
          </div>
        </div>
      </Card>

      {/* Resubmit banner (REJECTED) */}
      {isRejected && (
        <Card className="mb-6 flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:p-[18px_24px]">
          <div>
            <div className="text-sm font-semibold">Ready to resubmit?</div>
            <div className="mt-0.5 text-[13px] text-text-2">
              Correct your expenses and resubmit for admin review.
            </div>
          </div>
          <div className="flex w-full shrink-0 flex-col gap-1.5 sm:w-auto sm:items-end">
            <Button
              onClick={() => setModal({ type: 'resubmit-confirm' })}
              disabled={isResubmitting || hasNoItems}
              aria-describedby={hasNoItems ? 'empty-claim-hint' : undefined}
              className="w-full gap-2 sm:w-auto"
            >
              <RotateCw className={`h-4 w-4 ${isResubmitting ? 'animate-spin' : ''}`} strokeWidth={2} />
              {isResubmitting ? 'Resubmitting…' : 'Resubmit Claim'}
            </Button>
            {hasNoItems && <EmptyClaimHint />}
          </div>
        </Card>
      )}

      {resubmitErrors.length > 0 && (
        <div className="mb-6">
          <ValidationErrors errors={resubmitErrors} onDismiss={() => setResubmitErrors([])} />
        </div>
      )}

      {/* Ready to submit action bar (DRAFT) */}
      {isDraft && (
        <Card className="mb-6 flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:p-[18px_24px]">
          <div>
            <div className="text-sm font-semibold">Ready to submit?</div>
            <div className="mt-0.5 text-[13px] text-text-2">
              We'll validate your claim before sending it for review.
            </div>
          </div>
          <div className="flex w-full shrink-0 flex-col gap-1.5 sm:w-auto sm:items-end">
          <div className="flex w-full items-center gap-2.5 sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModal({ type: 'delete-claim-confirm' })}
              disabled={isSubmitting || deleteClaim.isPending}
              className="flex-1 gap-1.5 border-border text-danger hover:bg-danger-soft/50 sm:flex-none"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
              Delete
            </Button>
            <Button
              size="sm"
              onClick={() => setModal({ type: 'submit-confirm' })}
              disabled={isSubmitting || hasNoItems}
              aria-describedby={hasNoItems ? 'empty-claim-hint' : undefined}
              className="flex-1 gap-1.5 sm:flex-none"
            >
              <Send className="h-3.5 w-3.5" strokeWidth={2} />
              {isSubmitting ? 'Submitting…' : 'Submit Claim'}
            </Button>
          </div>
          {hasNoItems && <EmptyClaimHint />}
          </div>
        </Card>
      )}

      {validationErrors.length > 0 && (
        <div className="mb-6">
          <ValidationErrors errors={validationErrors} onDismiss={() => setValidationErrors([])} />
        </div>
      )}

      {deleteError && (
        <div className="mb-6 rounded-lg border border-danger/20 bg-danger-soft px-4 py-3 text-sm text-danger">
          {deleteError}
        </div>
      )}

      {/* Expenses header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center justify-between gap-2 sm:justify-start sm:gap-2.5">
          <h2 className="text-[17px] font-semibold">Expenses</h2>
          <span className="text-[13px] text-text-2">
            {plural(itemCount, 'item')}
          </span>
        </div>
        {isEditable && (
          <div className="flex items-center gap-2.5">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 gap-1.5 sm:flex-none"
              onClick={() => setModal({ type: 'petty-cash-create' })}
            >
              <ReceiptIcon className="h-3.5 w-3.5" strokeWidth={2} />
              {isMileageClaim ? 'Add Toll / Parking' : 'Add Petty Cash'}
            </Button>
            {canAddMileage && (
              <Button
                size="sm"
                className="flex-1 gap-1.5 sm:flex-none"
                onClick={() => setModal({ type: 'mileage-create' })}
              >
                <Car className="h-3.5 w-3.5" strokeWidth={2} />
                Add Mileage
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Expense items */}
      {expensesLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton h-16 rounded-lg" />
          ))}
        </div>
      ) : expensesData && expensesData.expenses.length > 0 ? (
        <div className="space-y-2">
          {expensesData.expenses.map((expense) => (
            <ExpenseRow
              key={expense.id}
              expense={expense}
              isEditable={isEditable}
              onEdit={() => {
                if (expense.type === 'mileage') {
                  setModal({ type: 'mileage-edit', entryId: expense.id });
                } else {
                  setModal({ type: 'petty-cash-edit', entryId: expense.id });
                }
              }}
              onDelete={() => {
                const label =
                  expense.type === 'mileage'
                    ? `${expense.origin} → ${expense.destination}`
                    : (expense as { description: string }).description;
                setModal({
                  type: 'delete-confirm',
                  expenseType: expense.type,
                  entryId: expense.id,
                  label,
                });
              }}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center rounded-lg border-[1.5px] border-dashed border-border bg-surface px-6 py-12 text-center">
          <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-surface-2 text-text-2">
            <ReceiptIcon className="h-6 w-6" strokeWidth={2} />
          </div>
          <h2 className="text-lg font-semibold">No expenses yet</h2>
          <p className="mx-auto mt-2 max-w-[360px] text-sm leading-relaxed text-text-2">
            {isEditable
              ? isMileageClaim
                ? 'Add a mileage trip, or a toll or parking expense, using the buttons above to get started.'
                : canAddMileage
                  ? 'Add a mileage trip or a petty cash expense using the buttons above to get started.'
                  : 'Add a petty cash expense using the button above to get started.'
              : 'This claim has no expenses.'}
          </p>
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {modal.type === 'mileage-create' && (
          <MileageFormModal key="mileage-create" claimId={claimId!} onClose={() => setModal({ type: 'none' })} />
        )}
        {modal.type === 'mileage-edit' && (
          <MileageFormModal
            key={`mileage-edit-${modal.entryId}`}
            claimId={claimId!}
            entryId={modal.entryId}
            readOnly={!isEditable}
            onClose={() => setModal({ type: 'none' })}
          />
        )}
        {modal.type === 'petty-cash-create' && (
          <PettyCashFormModal key="petty-cash-create" claimId={claimId!} claimType={claimType} onClose={() => setModal({ type: 'none' })} />
        )}
        {modal.type === 'petty-cash-edit' && (
          <PettyCashFormModal
            key={`petty-cash-edit-${modal.entryId}`}
            claimId={claimId!}
            entryId={modal.entryId}
            readOnly={!isEditable}
            onClose={() => setModal({ type: 'none' })}
          />
        )}
      </AnimatePresence>
      {modal.type === 'delete-confirm' && (
        <ConfirmDialog
          title={`Delete ${modal.expenseType === 'mileage' ? 'Mileage' : 'Petty Cash'} Entry`}
          message={`Are you sure you want to delete "${modal.label}"? This cannot be undone.`}
          confirmLabel="Delete"
          confirmVariant="destructive"
          isLoading={deleteMileage.isPending || deletePettyCash.isPending}
          onConfirm={() => handleDelete(modal.expenseType, modal.entryId)}
          onCancel={() => setModal({ type: 'none' })}
        />
      )}
      {modal.type === 'delete-claim-confirm' && (
        <ConfirmDialog
          title="Delete Claim"
          message="Are you sure you want to delete this entire claim? All expenses and receipts in it will be permanently removed. This cannot be undone."
          confirmLabel="Delete Claim"
          confirmVariant="destructive"
          isLoading={deleteClaim.isPending}
          onConfirm={handleDeleteClaim}
          onCancel={() => setModal({ type: 'none' })}
        />
      )}

      {/* Duplicate Submission Dialog */}
      {modal.type === 'duplicate-claim' && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-[rgb(15_23_42_/_0.4)]" onClick={() => setModal({ type: 'none' })} />
          <div className="relative w-full max-w-sm rounded-lg bg-surface p-6 shadow-[0_20px_25px_-5px_rgb(15_23_42_/_0.15),0_8px_10px_-6px_rgb(15_23_42_/_0.1)]">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-warn-soft text-warn">
                <AlertTriangle className="h-[18px] w-[18px]" strokeWidth={2} />
              </div>
              <div>
                <h3 className="text-base font-semibold">Already Submitted</h3>
                <p className="mt-1 text-sm text-text-2">
                  A claim for <span className="font-medium text-text">{modal.monthLabel}</span> has already
                  been submitted.
                  {modal.referenceNumber && (
                    <>
                      {' '}
                      (Ref: <span className="font-mono font-medium text-text">{modal.referenceNumber}</span>)
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="mt-5 flex gap-2.5">
              <Button variant="outline" className="flex-1" onClick={() => setModal({ type: 'none' })}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={() => navigate(`/claims/${modal.existingClaimId}`)}>
                View Existing Claim
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Submission Confirmation Modal with Physical Hardcopy Remark (Feedback #7, #8 / CAP-7) */}
      {(modal.type === 'submit-confirm' || modal.type === 'resubmit-confirm') && (
        <div
          role="dialog"
          aria-modal="true"
          id="submit-confirm-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
        >
          <div className="absolute inset-0 bg-surface/80 backdrop-blur-xs" onClick={() => setModal({ type: 'none' })} />
          <div className="relative w-full max-w-lg rounded-2xl border border-border bg-surface p-6 shadow-2xl space-y-5 animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Send className="h-5 w-5" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-text">
                    {modal.type === 'resubmit-confirm' ? 'Resubmit Claim for Review' : 'Submit Claim for Review'}
                  </h3>
                  <p className="text-xs text-text-2">
                    Please review the details below before submitting.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setModal({ type: 'none' })}
                className="p-1 rounded-md text-text-2 hover:text-text hover:bg-surface-2 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Claim Summary Box */}
            <div className="grid grid-cols-3 gap-2 rounded-xl bg-surface-2/40 p-3 text-center border border-border/50">
              <div>
                <p className="text-[11px] text-text-2 font-medium">Period</p>
                <p className="text-xs font-semibold text-text mt-0.5">{claim ? formatMonth(claim.claimMonth) : '-'}</p>
              </div>
              <div>
                <p className="text-[11px] text-text-2 font-medium">Total Items</p>
                <p className="text-xs font-semibold text-text mt-0.5">{plural(itemCount, 'item')}</p>
              </div>
              <div>
                <p className="text-[11px] text-text-2 font-medium">Total Amount</p>
                <p className="text-xs font-bold font-mono text-text mt-0.5">RM {formatCurrency(Number(claim?.totalAmount ?? 0))}</p>
              </div>
            </div>

            {/* Configured Hardcopy Remark Reminder Banner (Feedback #7, #8 / CAP-7) */}
            <div className="rounded-xl border border-warn/30 bg-warn-soft0/10 p-4 space-y-1.5">
              <div className="flex items-center gap-2 text-amber-800  font-semibold text-xs">
                <FileCheck className="h-4 w-4 shrink-0" />
                <span>Physical Hardcopy Receipt Reminder</span>
              </div>
              <p className="text-xs text-text leading-relaxed pl-6">
                {systemConfigData?.config?.hardcopyRemark ??
                  'Please ensure all original physical receipts and claim forms are printed, attached, and submitted to the Finance department.'}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setModal({ type: 'none' })}
                disabled={isSubmitting || isResubmitting}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="gap-1.5"
                onClick={async () => {
                  const isResubmit = modal.type === 'resubmit-confirm';
                  setModal({ type: 'none' });
                  if (isResubmit) {
                    await handleResubmit();
                  } else {
                    await handleSubmit();
                  }
                }}
                disabled={isSubmitting || isResubmitting || hasNoItems}
              >
                <Send className="h-3.5 w-3.5" />
                {isSubmitting || isResubmitting ? 'Submitting…' : 'Confirm & Submit'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// Expense Row
// ---------------------------------------------------------------------------

interface ExpenseRowProps {
  expense: ExpenseItem;
  isEditable: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function ExpenseRow({ expense, isEditable, onEdit, onDelete }: ExpenseRowProps) {
  const isMileage = expense.type === 'mileage';
  const mileage = isMileage ? (expense as { purpose?: string; origin: string; destination: string; isRoundTrip: boolean; finalDestination?: string | null }) : null;
  const pettyCash = isMileage ? null : (expense as { description: string; category?: { name: string } });
  const title = isMileage ? (mileage!.purpose || `${mileage!.origin} → ${mileage!.destination}`) : pettyCash!.description;

  return (
    <div
      id={`expense-${expense.id}`}
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onEdit();
        }
      }}
      className={`group flex cursor-pointer flex-wrap items-start rounded-lg border px-4 py-3 sm:flex-nowrap sm:items-center ${expense.flagReason ? 'border-warn/50 bg-warn-soft' : 'border-border bg-surface'} transition-colors hover:border-primary/40 hover:bg-surface-2/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
      aria-label={`View ${isMileage ? 'mileage' : 'petty cash'} expense details`}
    >
      <div className="mr-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-2 text-text-2">
        {isMileage ? <Car className="h-4 w-4" strokeWidth={2} /> : <ReceiptIcon className="h-4 w-4" strokeWidth={2} />}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="line-clamp-2 break-words text-sm font-semibold text-text">
            {title}
          </p>
          {expense.adminVerified && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-info-soft text-info border border-info/20">
              <Check className="h-2.5 w-2.5" /> Admin
            </span>
          )}
          {expense.financeVerified && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-info-soft text-info border border-info/20">
              <Check className="h-2.5 w-2.5" /> Finance
            </span>
          )}
          {expense.hrVerified && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-ok-soft text-ok border border-ok/20">
              <Check className="h-2.5 w-2.5" /> HR
            </span>
          )}
          {expense.flagReason && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-warn-soft text-warn border border-warn/30">
              <Flag className="h-2.5 w-2.5" /> Flagged by {describeFlagger(expense.flaggedBy)}
            </span>
          )}
        </div>
        {isMileage && (
          <p className="mt-0.5 text-xs text-text-2 break-words leading-relaxed">
            <span className="text-text/90">{mileage!.origin}</span>
            <span className="mx-1.5 font-bold text-primary">→</span>
            <span className="text-text/90">{mileage!.destination}</span>
            {mileage!.isRoundTrip && (
              <>
                <span className="mx-1.5 font-bold text-primary">→</span>
                <span className="text-text/90">{mileage!.finalDestination ?? mileage!.origin}</span>
              </>
            )}
          </p>
        )}
        <p className="mt-0.5 text-xs text-text-2">
          {formatDate(expense.date)}
          {!isMileage && pettyCash!.category && ` · ${pettyCash!.category.name}`}
          {isMileage && mileage!.isRoundTrip && ' · Round trip'}
        </p>
        {expense.flagReason && (
          <p className="text-[11px] text-warn italic mt-0.5">
            "{expense.flagReason}"
          </p>
        )}
      </div>

      {/* Below sm the amount + actions drop to their own row so the route text keeps the full width */}
      <div className="mt-2 flex w-full items-center justify-between pl-12 sm:ml-3 sm:mt-0 sm:w-auto sm:shrink-0 sm:gap-3 sm:pl-0">
      <div className="text-left sm:text-right">
        <p className="font-mono text-sm font-semibold">RM {formatCurrency(Number(expense.amount))}</p>
        {isMileage && (
          <p className="text-xs text-text-2">
            {Number((expense as { claimableDistance: number }).claimableDistance).toFixed(2)} km
          </p>
        )}
      </div>

      {isEditable && (
        <div className="flex shrink-0 gap-1">
          <button
            onClick={(event) => {
              event.stopPropagation();
              onEdit();
            }}
            className="rounded-md p-2 text-text-2 transition-colors hover:bg-surface-2 hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-1.5"
            aria-label="Edit entry"
          >
            <Pencil className="h-4 w-4" strokeWidth={2} />
          </button>
          <button
            onClick={(event) => {
              event.stopPropagation();
              onDelete();
            }}
            className="rounded-md p-2 text-text-2 transition-colors hover:bg-danger-soft hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-1.5"
            aria-label="Delete entry"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      )}
      </div>
    </div>
  );
}
